import { Callout } from "@blueprintjs/core";

import { RichContentRenderer } from "@/components/helpers/ui/RichContentRenderer";
import { Box } from "@/components/layout/flexbox";
import { isValidHttpOrHttpsUrl } from "@/components/lib/url";
import { buildComponentMetadata } from "@/components/pages_logged_in/program/components/componentMetadata";
import { ProgramRoleplayCard } from "@/components/pages_logged_in/program/components/ProgramRoleplayCard";
import { ParticipantComponentStatistic } from "@/components/pages_logged_in/program/components/program-component/ParticipantComponentStatistic";
import { ProgramComponentDateInfo } from "@/components/pages_logged_in/program/components/program-component/ProgramComponentDateInfo";
import { ProgramComponentStatusInfo } from "@/components/pages_logged_in/program/components/program-component/ProgramStatusInfo";
import { MeetingButton } from "@/components/pages_logged_in/program/components/program-component/panels";
import { UrlLink } from "@/components/pages_logged_in/program/components/program-component/UrlLink";
import { OneOneSessionStats } from "@/components/pages_logged_in/program/components/set-components/OneOneSessionStats";
import { canViewComponentStatistic } from "@/components/pages_logged_in/program/components/utils";
import { LinkPreview } from "@/components/pieces/link/LinkPreview";
import { VideoEmbed } from "@/components/pieces/videoEmbed";
import { CARD_CLASSES, SUBTEXT_COLOR } from "@/css/constants";
import { ComponentTypeEnum, useGetGeneralStatisticsQuery } from "@/graphql";
import { cn } from "@/lib/utils";
import {
  MainComponent,
  MainProgram,
  StatisticComponent,
} from "@/types/programs";
import { ProgramScenario } from "@/types/roleplays";

interface ComponentSummaryProps {
  program: MainProgram;
  component: MainComponent;
  statisticComponent: StatisticComponent;
  isProgramLead: boolean;
  roleplayScenarios: ProgramScenario[];
}

export function ComponentSummary({
  program,
  component,
  statisticComponent,
  isProgramLead,
  roleplayScenarios,
}: ComponentSummaryProps) {
  const metaData = buildComponentMetadata({
    component: statisticComponent,
    isProgramLead,
  });

  const { participantGuidance } = statisticComponent || {};

  const canViewStatistic = canViewComponentStatistic(component, isProgramLead);

  const formFields = statisticComponent?.survey?.formFields || [];
  const mainFormField = formFields?.[0] || null;
  const url = mainFormField?.link?.url || "";
  const urlText = mainFormField?.link?.text || "";

  const mergedComponent =
    component && statisticComponent
      ? { ...component, ...statisticComponent }
      : component || statisticComponent;

  const { data } = useGetGeneralStatisticsQuery({
    variables: {
      programComponentId: component.id,
    },
    skip: !metaData.isOneOneMaster,
  });

  const {
    totalParticipants,
    onTrackPercentage,
    averageRating,
    completedSessionsCount,
    totalCreditsSpent,
    activeCoachesCount,
    ratingCount,
  } = data?.generalStatistics || {};

  return (
    <Box
      className={cn(
        CARD_CLASSES,
        "xs:p-4 xs:gap-5 mt-6 flex flex-1 flex-col bg-white md:gap-6 md:p-6",
      )}
    >
      <Box fontSize={18} fontWeight={600}>
        Progress
      </Box>
      {statisticComponent === null && (
        <Box mt={24}>
          <Callout intent="danger" icon={null}>
            No component found
          </Callout>
        </Box>
      )}
      <ProgramComponentStatusInfo component={mergedComponent} role="lead" />
      {metaData.isOneOneMaster && (
        <OneOneSessionStats
          totalParticipants={totalParticipants ?? 0}
          onTrackPercentage={onTrackPercentage ?? 0}
          averageRating={averageRating ?? 0}
          completedSessionsCount={completedSessionsCount ?? 0}
          totalCreditsSpent={totalCreditsSpent ?? 0}
          activeCoachesCount={activeCoachesCount ?? 0}
          ratingCount={ratingCount ?? 0}
        />
      )}
      {canViewStatistic && (
        <ParticipantComponentStatistic
          component={mergedComponent}
          program={program}
          isProgramLead={isProgramLead}
          canShowViewDetailButton={false}
          hideStarted={
            statisticComponent?.type === ComponentTypeEnum.Video ||
            statisticComponent?.type === ComponentTypeEnum.Article
          }
        />
      )}
      <ProgramComponentDateInfo
        component={component}
        isProgramLead={isProgramLead}
      />
      {!!participantGuidance && (
        <div className="flex flex-col gap-2">
          <Box color={SUBTEXT_COLOR}>Guidance Given to Participants</Box>
          <RichContentRenderer content={participantGuidance} />
        </div>
      )}
      {metaData.isVideo && url && (
        <VideoEmbed
          url={url}
          altComponent={<UrlLink url={url} text={urlText} />}
        />
      )}
      {metaData.isArticle && isValidHttpOrHttpsUrl(url) && (
        <LinkPreview
          url={url}
          isEditable={false}
          metadata={{
            text: mainFormField?.link?.text || "",
            imageSrc: mainFormField?.link?.imageSrc,
          }}
        />
      )}
      {metaData.isGroupMeeting && isValidHttpOrHttpsUrl(url) && (
        <MeetingButton url={url} />
      )}
      {metaData.isAIRoleplay && roleplayScenarios && (
        <ProgramRoleplayCard
          roleplayScenarios={roleplayScenarios}
          canDoRoleplay={false}
          hideRoleplayRank={true}
          large
          isProgramLead={isProgramLead}
          isProgramClosed={!!program.closedAt}
          scenarioRequiredNum={component.scenarioRequiredNum}
          minimumRank={component.scenarioRankMin}
          minimumAttempts={component.scenarioAttemptMin}
          maximumAttempts={component.scenarioAttemptMax}
        />
      )}
    </Box>
  );
}
